import { getIDFromUrl } from '@rossum/api-client';
import { IconBrain } from '@rossum/ui/icons/tabler';
import { Link, Stack, SvgIcon, Tooltip, Typography } from '@rossum/ui/material';
import { truncate } from 'lodash';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
import { PageLayoutV2 } from '../../../../components/PageLayoutV2/PageLayoutV2';
import { useGetInbox } from '../../../../features/document-upload/hooks/useGetInbox';
import { useEngine } from '../../../../features/engines/hooks/useEngine';
import { engineBasicSettingsPath } from '../../../../features/engines/paths';
import { useDedicatedEngine } from '../../../../features/legacy-engines/hooks/useDedicatedEngine';
import { useGenericEngine } from '../../../../features/legacy-engines/hooks/useGenericEngine';
import { BasicSettingsHeader } from '../../../../features/queue-settings/headers/BasicSettingsHeader';
import { useQueueWithWorkspace } from '../../../../features/queues/hooks/useQueueWithWorkspace';
import { auroraEngineEnabledSelector } from '../../../../redux/modules/organizationGroup/selectors';
import { fetchSchema } from '../../../../redux/modules/schema/actions';
import { shouldDisplayAuroraChipByName } from '../../../../ui/aurora/AuroraChip';
import { AuroraIcon } from '../../../../ui/aurora/Icons';
import { automationEnginesPath } from '../../../Automation/helpers';
import { StatCard } from '../../../Settings/components/StatCard';
import QueueStats from '../../../Settings/containers/QueueStats';
import { EmailAddress } from '../../components/EmailAddress';
import { WorkspaceQueueBreadcrumbs } from '../../components/WorkspaceQueueBreadcrumbs';
import { QueueBasicSettingsContainer } from './components/QueueBasicSettingsContainer';

type QueueBasicSettingsProps = RouteComponentProps<{ queueId: string }>;

const truncationLimit = 30;

export const QueueBasicSettings = ({
  match: {
    url,
    params: { queueId },
  },
}: QueueBasicSettingsProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { queue, workspace } = useQueueWithWorkspace({
    queueId: Number(queueId),
  });

  const { data: inbox } = useGetInbox(
    queue && queue.inbox ? getIDFromUrl(queue?.inbox) : 0
  );

  // <queue-is-only-allowed-to-have-one-engine-so-we-condition-these-queries>

  const genericEngineId = queue?.genericEngine
    ? getIDFromUrl(queue.genericEngine)
    : null;
  const { data: genericEngine } = useGenericEngine(genericEngineId);

  const dedicatedEngineId =
    !genericEngineId && queue?.dedicatedEngine
      ? getIDFromUrl(queue.dedicatedEngine)
      : null;

  const { data: dedicatedEngine } = useDedicatedEngine(dedicatedEngineId);

  const auroraEngineEnabled = useSelector(auroraEngineEnabledSelector);

  const engineId =
    !dedicatedEngineId && queue?.engine ? getIDFromUrl(queue.engine) : null;

  const { data: engineData } = useEngine(engineId);

  // </queue-is-only-allowed-to-have-one-engine-so-we-condition-these-queries>

  useEffect(() => {
    if (queue?.schema) {
      dispatch(fetchSchema(queue.schema));
    }
  }, [queue?.schema, dispatch]);

  if (!queue) return null;

  const { engine, enginePath } = {
    engine: dedicatedEngine ?? genericEngine ?? engineData,
    enginePath: engineData
      ? engineBasicSettingsPath(engineData.id)
      : automationEnginesPath(),
  };

  const description = engine ? engine.description : '-';

  const truncatedDescription = truncate(description, {
    length: truncationLimit,
    omission: '… ',
  });

  const inboxAddress = inbox?.email;

  const engineName = engine ? engine.name : '-';

  const shouldDisplayAuroraChip = shouldDisplayAuroraChipByName(
    engineName,
    auroraEngineEnabled
  );

  return (
    <PageLayoutV2
      renderHeader={params => (
        <BasicSettingsHeader
          scrollableDivRef={params.scrollableDivRef}
          queueName={queue.name}
          emailAddress={inboxAddress}
          parentPath={url}
        />
      )}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 4 }}
      >
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <WorkspaceQueueBreadcrumbs
            workspace={workspace?.name}
            queue={queue.name}
          />
          <EmailAddress inboxAddress={inboxAddress} />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ my: 2, flexWrap: 'wrap' }}
      >
        <Stack sx={{ flexGrow: 1, flexBasis: '30%' }}>
          <StatCard
            icon={
              shouldDisplayAuroraChip ? (
                <AuroraIcon color="primary" />
              ) : (
                <SvgIcon
                  component={IconBrain}
                  sx={{ fill: 'none' }}
                  color="primary"
                />
              )
            }
            title={engineName}
            titleDataCy="stats-value-engine"
            description={
              <Typography variant="caption" color="text.disabled">
                <Tooltip
                  placement="top"
                  enterDelay={500}
                  title={
                    truncatedDescription !== description ? description : ''
                  }
                  arrow
                >
                  <span>{truncatedDescription}</span>
                </Tooltip>
                <Link
                  component={RouterLink}
                  to={enginePath}
                  sx={{
                    ml: 0.5,
                    color: 'inherit',
                    textDecorationColor: 'inherit',
                    '&:hover': {
                      color: 'inherit',
                      textDecoration: 'none',
                    },
                  }}
                >
                  {intl.formatMessage({
                    id: 'containers.settings.queues.engine.linkMore',
                  })}
                </Link>
              </Typography>
            }
          />
        </Stack>
        {queue ? <QueueStats selectedQueue={queue} /> : null}
      </Stack>
      <QueueBasicSettingsContainer selectedQueue={queue} inbox={inbox} />
    </PageLayoutV2>
  );
};
